/**
 * OpenLayers layers for Pennsylvania POTA research map.
 */

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import LayerGroup from 'ol/layer/Group';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Stroke, Fill, Circle } from 'ol/style.js';

import { hatchFillColor } from '../lib/hatch-pattern.js';

const geoJsonFormat = new GeoJSON();

export const LAYER_IDS = {
    POTA_POINTS: 'pota-points',
    ACTIVATION: 'activation-geometries',
    TRAIL_BUFFERS: 'trail-buffers',
    TRAIL_LINES: 'trail-lines',
    PARK_BOUNDARIES: 'park-boundaries',
    POTA_PARK_BOUNDARIES: 'pota-park-boundaries',
    STATE_GAMELAND_BOUNDARIES: 'state-gameland-boundaries',
    CANDIDATES_2FER: 'candidates-2fer',
    CANDIDATES_NFER: 'candidates-nfer',
    ACCESS: 'access-points',
};

function readGeoJsonFeatures(data, projection) {
    return geoJsonFormat.readFeatures(data, {
        dataProjection: 'EPSG:4326',
        featureProjection: projection,
    });
}

function confidenceStroke(confidence) {
    if (confidence === 'low') {
        return [8, 6];
    }
    return [];
}

function confidenceColor(confidence, alpha = 1) {
    switch (confidence) {
        case 'high':
            return `rgba(34, 139, 34, ${alpha})`;
        case 'medium':
            return `rgba(218, 165, 32, ${alpha})`;
        case 'low':
            return `rgba(220, 20, 60, ${alpha})`;
        default:
            return `rgba(100, 100, 100, ${alpha})`;
    }
}

/**
 * @param {import('ol/proj/Projection').default} projection
 * @param {{
 *   passesFilter: (props: object) => boolean,
 *   getSelectedCandidateId?: () => string|null,
 * }} filterState
 */
export function createPaResearchLayers(projection, filterState) {
    const potaSource = new VectorSource();
    const activationSource = new VectorSource();
    const trailBufferSource = new VectorSource();
    const trailLineSource = new VectorSource();
    const boundarySource = new VectorSource();
    const potaParkBoundarySource = new VectorSource();
    const stateGamelandBoundarySource = new VectorSource();
    const candidate2Source = new VectorSource();
    const candidateNSource = new VectorSource();
    const accessSource = new VectorSource();

    function isSelectedCandidate(props) {
        const selectedId = filterState.getSelectedCandidateId?.();
        return Boolean(selectedId && props.candidateId === selectedId);
    }

    function isRejected(props) {
        return props.reviewStatus === 'rejected' || props.communityRejected;
    }

    function overlapStyles(props, { fillAlpha, strokeWidth, nfer = false }) {
        const confidence = props.confidence || 'medium';
        const selected = isSelectedCandidate(props) && props.featureKind === 'overlap';
        const rejected = isRejected(props) && props.featureKind === 'overlap';

        if (selected) {
            const lineColor = nfer ? 'rgba(90, 40, 160, 0.95)' : confidenceColor(confidence, 0.95);
            const bgColor = nfer ? 'rgba(120, 60, 200, 0.35)' : confidenceColor(confidence, 0.28);
            return [
                new Style({
                    fill: new Fill({
                        color: hatchFillColor(lineColor, bgColor, { size: 12, lineWidth: 3 }),
                    }),
                    stroke: new Stroke({
                        color: lineColor,
                        width: strokeWidth + 1.5,
                        lineDash: confidenceStroke(confidence),
                    }),
                    zIndex: 25,
                }),
            ];
        }

        if (rejected) {
            return new Style({
                fill: new Fill({ color: 'rgba(180, 60, 60, 0.28)' }),
                stroke: new Stroke({
                    color: 'rgba(160, 40, 40, 0.9)',
                    width: strokeWidth,
                    lineDash: [8, 5],
                }),
                zIndex: 18,
            });
        }

        return new Style({
            fill: new Fill({ color: confidenceColor(confidence, fillAlpha) }),
            stroke: new Stroke({
                color: confidenceColor(confidence, 0.95),
                width: strokeWidth,
                lineDash: confidenceStroke(confidence),
            }),
            zIndex: 20,
        });
    }

    function candidateStyle(fillAlpha, strokeWidth) {
        return (feature) => {
            const props = feature.getProperties();
            if (!filterState.passesFilter(props)) {
                return null;
            }

            if (props.featureKind === 'setup-point') {
                const confidence = props.confidence || 'medium';
                const selected = isSelectedCandidate(props);
                const rejected = isRejected(props);
                const fill = rejected ? 'rgba(180, 60, 60, 0.9)' : confidenceColor(confidence, 0.95);
                return new Style({
                    image: new Circle({
                        radius: selected ? (props.n >= 3 ? 11 : 9) : (props.n >= 3 ? 9 : 7),
                        fill: new Fill({ color: fill }),
                        stroke: new Stroke({
                            color: selected ? '#ffdd00' : '#fff',
                            width: selected ? 3 : 2,
                        }),
                    }),
                    zIndex: selected ? 35 : 30,
                });
            }

            return overlapStyles(props, { fillAlpha, strokeWidth });
        };
    }

    const layers = {
        pota: new VectorLayer({
            source: potaSource,
            title: 'POTA points',
            visible: true,
            zIndex: 22,
            style: new Style({
                image: new Circle({
                    radius: 4,
                    fill: new Fill({ color: 'rgba(0, 90, 200, 0.9)' }),
                    stroke: new Stroke({ color: '#fff', width: 1 }),
                }),
            }),
        }),
        activation: new VectorLayer({
            source: activationSource,
            title: 'Activation geometries',
            visible: false,
            zIndex: 15,
            style: (feature) => {
                const props = feature.getProperties();
                if (props.activationGeometryType === 'buffered_line') {
                    return null;
                }
                const low = props.matchConfidence === 'low' || props.matchMethod?.includes('fuzzy');
                return new Style({
                    fill: new Fill({ color: low ? 'rgba(180, 80, 80, 0.15)' : 'rgba(80, 120, 180, 0.18)' }),
                    stroke: new Stroke({
                        color: low ? 'rgba(160, 60, 60, 0.7)' : 'rgba(60, 90, 140, 0.8)',
                        width: 1.5,
                        lineDash: low ? [6, 4] : [],
                    }),
                });
            },
        }),
        trailBuffers: new VectorLayer({
            source: trailBufferSource,
            title: 'Trail 100 ft buffers',
            visible: true,
            zIndex: 16,
            style: new Style({
                fill: new Fill({ color: 'rgba(204, 51, 115, 0.22)' }),
                stroke: new Stroke({ color: 'rgba(180, 30, 90, 0.85)', width: 2 }),
            }),
        }),
        trailLines: new VectorLayer({
            source: trailLineSource,
            title: 'Trail centerlines',
            visible: true,
            zIndex: 17,
            style: new Style({
                stroke: new Stroke({ color: 'rgba(140, 20, 70, 0.9)', width: 2 }),
            }),
        }),
        boundaries: new VectorLayer({
            source: boundarySource,
            title: 'Park boundaries (legacy)',
            visible: false,
            zIndex: 10,
            style: new Style({
                fill: new Fill({ color: 'rgba(120, 120, 120, 0.06)' }),
                stroke: new Stroke({ color: 'rgba(80, 80, 80, 0.5)', width: 1 }),
            }),
        }),
        potaParkBoundaries: new VectorLayer({
            source: potaParkBoundarySource,
            title: 'State park boundaries',
            visible: false,
            zIndex: 11,
            style: new Style({
                fill: new Fill({ color: 'rgba(34, 139, 34, 0.14)' }),
                stroke: new Stroke({ color: 'rgba(20, 100, 20, 0.85)', width: 2 }),
            }),
        }),
        stateGamelandBoundaries: new VectorLayer({
            source: stateGamelandBoundarySource,
            title: 'State game land boundaries',
            visible: false,
            zIndex: 12,
            style: new Style({
                fill: new Fill({ color: 'rgba(180, 110, 30, 0.14)' }),
                stroke: new Stroke({ color: 'rgba(130, 75, 15, 0.85)', width: 2 }),
            }),
        }),
        candidates2: new VectorLayer({
            source: candidate2Source,
            title: '2-fer candidates',
            visible: true,
            zIndex: 20,
            style: candidateStyle(0.42, 2),
        }),
        candidatesN: new VectorLayer({
            source: candidateNSource,
            title: '3-fer+ candidates',
            visible: true,
            zIndex: 21,
            style: (feature) => {
                const props = feature.getProperties();
                if (!filterState.passesFilter(props)) {
                    return null;
                }
                if (props.featureKind === 'setup-point') {
                    const selected = isSelectedCandidate(props);
                    const rejected = isRejected(props);
                    const fill = rejected ? 'rgba(180, 60, 60, 0.9)' : 'rgba(120, 60, 200, 0.95)';
                    return new Style({
                        image: new Circle({
                            radius: selected ? 12 : 10,
                            fill: new Fill({ color: fill }),
                            stroke: new Stroke({
                                color: selected ? '#ffdd00' : '#fff',
                                width: selected ? 3 : 2,
                            }),
                        }),
                        zIndex: selected ? 36 : 31,
                    });
                }
                return overlapStyles(props, { fillAlpha: 0.38, strokeWidth: 2.5, nfer: true });
            },
        }),
        access: new VectorLayer({
            source: accessSource,
            title: 'Parking / trailheads',
            visible: false,
            zIndex: 12,
            style: (feature) => {
                const kind = feature.get('accessKind');
                const color = kind === 'trailhead'
                    ? 'rgba(0, 130, 90, 0.9)'
                    : 'rgba(30, 30, 30, 0.85)';
                return new Style({
                    image: new Circle({
                        radius: 5,
                        fill: new Fill({ color }),
                        stroke: new Stroke({ color: '#fff', width: 1 }),
                    }),
                });
            },
        }),
    };

    const group = new LayerGroup({
        title: 'PA research',
        layers: [
            layers.pota,
            layers.activation,
            layers.trailBuffers,
            layers.trailLines,
            layers.boundaries,
            layers.potaParkBoundaries,
            layers.stateGamelandBoundaries,
            layers.candidates2,
            layers.candidatesN,
            layers.access,
        ],
    });

    function candidatePropsForFeature(candidate, extra = {}) {
        const { geometry, ...rest } = candidate;
        return { ...rest, ...extra };
    }

    function addCandidateFeatures(source, candidate, kind) {
        if (candidate.geometry) {
            const overlap = geoJsonFormat.readFeature(candidate.geometry, {
                dataProjection: 'EPSG:4326',
                featureProjection: projection,
            });
            overlap.setProperties(candidatePropsForFeature(candidate, {
                featureKind: 'overlap',
                candidateKind: kind,
            }), true);
            source.addFeature(overlap);
        }

        if (candidate.setupPoint) {
            const spot = geoJsonFormat.readFeature({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [candidate.setupPoint.lon, candidate.setupPoint.lat],
                },
                properties: {},
            }, {
                dataProjection: 'EPSG:4326',
                featureProjection: projection,
            });
            spot.setProperties(candidatePropsForFeature(candidate, {
                featureKind: 'setup-point',
                candidateKind: kind,
            }), true);
            source.addFeature(spot);
        }
    }

    async function loadJson(url) {
        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }
        return response.json();
    }

    async function loadTrailLines(locId) {
        const registry = await loadJson(`./data/${locId}/trail-registry-${locId}.json`);
        if (!registry?.trails?.length) {
            return 0;
        }

        let count = 0;
        for (const trail of registry.trails) {
            const relative = trail.sourceFile.replace(/^US-PA\//, '');
            if (!relative.startsWith('sources/')) {
                continue;
            }
            const geojson = await loadJson(`./data/${locId}/${relative}`);
            if (!geojson?.features?.length) {
                continue;
            }
            const features = readGeoJsonFeatures(geojson, projection);
            for (const feature of features) {
                feature.set('trailRef', trail.ref);
                feature.set('trailTitle', trail.title);
            }
            trailLineSource.addFeatures(features);
            count += features.length;
        }
        return count;
    }

    async function loadAll(locId) {
        const stats = {
            pota: 0,
            activation: 0,
            trailBuffers: 0,
            trailLines: 0,
            boundaries: 0,
            potaParkBoundaries: 0,
            stateGamelandBoundaries: 0,
            candidates2: 0,
            candidatesN: 0,
            access: 0,
        };

        const parks = await loadJson(`./data/${locId}/parks-${locId}.geojson`);
        if (parks?.features?.length) {
            const features = readGeoJsonFeatures(parks, projection);
            potaSource.addFeatures(features);
            stats.pota = features.length;
        }

        const activations = await loadJson(`./data/${locId}/activation-geometries-${locId}.geojson`);
        if (activations?.features?.length) {
            const features = readGeoJsonFeatures(activations, projection);
            for (const feature of features) {
                const geomType = feature.get('activationGeometryType');
                if (geomType === 'buffered_line') {
                    trailBufferSource.addFeature(feature);
                    stats.trailBuffers++;
                    continue;
                }

                const refType = feature.get('refType');
                if (refType === 'state_park') {
                    potaParkBoundarySource.addFeature(feature);
                    stats.potaParkBoundaries++;
                } else if (refType === 'state_game_land') {
                    stateGamelandBoundarySource.addFeature(feature);
                    stats.stateGamelandBoundaries++;
                } else {
                    activationSource.addFeature(feature);
                    stats.activation++;
                }
            }
        }

        stats.trailLines = await loadTrailLines(locId);

        const boundaries = await loadJson(`./data/${locId}/park-boundaries-${locId}.geojson`);
        if (boundaries?.features?.length) {
            const features = readGeoJsonFeatures(boundaries, projection);
            boundarySource.addFeatures(features);
            stats.boundaries = features.length;
        }

        const twoFers = await loadJson(`./data/${locId}/candidate-2fers-${locId}.json`);
        if (twoFers?.candidates?.length) {
            for (const candidate of twoFers.candidates) {
                addCandidateFeatures(candidate2Source, candidate, '2fer');
            }
            stats.candidates2 = twoFers.candidates.length;
        }

        const nFers = await loadJson(`./data/${locId}/candidate-3fers-${locId}.json`)
            || await loadJson(`./data/${locId}/candidate-nfers-${locId}.json`);
        if (nFers?.candidates?.length) {
            for (const candidate of nFers.candidates) {
                addCandidateFeatures(candidateNSource, candidate, 'nfer');
            }
            stats.candidatesN = nFers.candidates.length;
        }

        for (const [file, kind] of [['parking.geojson', 'parking'], ['trailheads.geojson', 'trailhead']]) {
            const layer = await loadJson(`./data/${locId}/sources/osm/${file}`);
            if (!layer?.features?.length) {
                continue;
            }
            const features = readGeoJsonFeatures(layer, projection);
            for (const feature of features) {
                feature.set('accessKind', kind);
            }
            accessSource.addFeatures(features);
            stats.access += features.length;
        }

        return stats;
    }

    function refreshCandidateStyles() {
        layers.candidates2.changed();
        layers.candidatesN.changed();
    }

    return {
        group,
        layers,
        parkLayers: [
            layers.pota,
            layers.activation,
            layers.trailBuffers,
            layers.boundaries,
            layers.potaParkBoundaries,
            layers.stateGamelandBoundaries,
        ],
        sources: {
            candidate2: candidate2Source,
            candidateN: candidateNSource,
        },
        loadAll,
        refreshCandidateStyles,
    };
}
